import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  PageActions,
  PageTitle,
} from "@/components/atomic-crm/layout/PageActions";
import { cn } from "@/lib/utils";
import { MODULE_LIST, type NomiModule } from "@/modules/registry";
import { MODULE_CATALOG } from "@/modules/apps/moduleCatalog";
import { isOn } from "@/modules/apps/moduleDependencies";
import { useOrgModuleRows } from "@/modules/apps/useOrgModuleRows";
import { useModuleSetup } from "@/modules/apps/useModuleSetup";
import type { ModuleRow } from "@/modules/apps/ModulesManager";

/** App store: every module, what it does and whether it's on. */
export const AppsPage = () => {
  const { rows, isPending } = useOrgModuleRows();
  const installed = MODULE_LIST.filter((module) => isOn(rows, module.id));
  const available = MODULE_LIST.filter((module) => !isOn(rows, module.id));

  return (
    <div className="flex w-full min-w-0 flex-col gap-6">
      <PageActions>
        <PageTitle label="Apps" />
      </PageActions>
      <p className="max-w-2xl text-sm text-muted-foreground">
        Add the parts of Sigma your business needs. Open an app to see what it
        does, how it works with the rest and what it needs to get started.
      </p>

      {isPending ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
      ) : (
        <>
          <AppSection title="Your apps" modules={installed} rows={rows} />
          {available.length ? (
            <AppSection title="Add more apps" modules={available} rows={rows} />
          ) : null}
        </>
      )}
    </div>
  );
};

const AppSection = ({
  title,
  modules,
  rows,
}: {
  title: string;
  modules: NomiModule[];
  rows: ModuleRow[];
}) => (
  <section className="flex flex-col gap-3">
    <h2 className="text-sm font-semibold text-muted-foreground">{title}</h2>
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {modules.map((module) => (
        <AppCard key={module.id} module={module} on={isOn(rows, module.id)} />
      ))}
    </div>
  </section>
);

const AppCard = ({ module, on }: { module: NomiModule; on: boolean }) => {
  const Icon = module.icon;
  const setup = useModuleSetup(on ? module.id : null);
  const needsSetup = on && !setup.isPending && !setup.isReady;
  return (
    <Link
      to={`/apps/${module.id}`}
      className={cn(
        "group flex gap-3 rounded-xl border bg-card p-4 transition-colors hover:border-primary/50 hover:bg-primary/5",
        "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
      )}
    >
      <span
        className={cn(
          "flex size-11 shrink-0 items-center justify-center rounded-xl",
          on ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
        )}
      >
        <Icon className="size-6" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{module.name}</span>
          {module.core ? (
            <Badge variant="secondary">Included</Badge>
          ) : needsSetup ? (
            <Badge variant="warning">
              Set up {setup.requiredDone}/{setup.requiredTotal}
            </Badge>
          ) : on ? (
            <Badge variant="success">On</Badge>
          ) : null}
        </span>
        <span className="mt-1 block text-sm text-muted-foreground">
          {MODULE_CATALOG[module.id].summary}
        </span>
      </span>
    </Link>
  );
};
